// src/api/users.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '@/config/service';

const getUsers = async () => {
  const response = await API.get('/users');
  return response.data;
};

export const useGetUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });
};

const getUser = async (userId) => {
  if (!userId) {
    throw new Error('User ID is missing');
  }
  const response = await API.get(`/users/${userId}`);
  return response.data.data;
};

export const useGetUser = (userId) => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId),
    enabled: !!userId,
  });
};

const createUser = async (userData) => {
  const response = await API.post('/users', userData);
  return response.data;
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });
};

const updateUser = async ({ userId, data }) => {
  if (!userId) {
    throw new Error('User ID is missing');
  }
  const response = await API.put(`/users/${userId}`, data);
  return response.data;
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });
};

const deleteUser = async (userId) => {
  if (!userId) {
    throw new Error('User ID is missing');
  }
  await API.delete(`/users/${userId}`);
  return userId;
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });
};

const changeUserStatus = async ({ userId, status }) => {
  if (!userId) {
    throw new Error('User ID is missing');
  }
  const response = await API.patch(`/users/${userId}/status`, { status });
  return response.data;
};

export const useChangeUserStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: changeUserStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });
};

const changePassword = async ({ userId, newPassword, confirmPassword }) => {
  if (!userId) {
    throw new Error('User ID is missing');
  }
  const response = await API.patch(`/users/${userId}/password`, {
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
  return response.data;
};

export const useChangePassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });
};

// getUserByUuid is the same as getUser
export const useGetUserByUuid = useGetUser;